import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Globe, Link as LinkIcon, FileText } from 'lucide-react';

const Content = () => {
  const { API, fetchSettings } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_text: { sq: '', de: '', en: '' },
    about_text: { sq: '', de: '', en: '' },
    cta_text: { sq: '', de: '', en: '' },
    footer_text: { sq: '', de: '', en: '' },
    social_links: {
      youtube: '',
      instagram: '',
      spotify: '',
      tiktok: '',
      soundcloud: '',
      twitter: ''
    },
    seo_title: { sq: '', de: '', en: '' },
    seo_description: { sq: '', de: '', en: '' }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
        setSettings(prev => ({
          ...prev,
          hero_text: response.data.hero_text || prev.hero_text,
          about_text: response.data.about_text || prev.about_text,
          cta_text: response.data.cta_text || prev.cta_text,
          footer_text: response.data.footer_text || prev.footer_text,
          social_links: response.data.social_links || prev.social_links,
          seo_title: response.data.seo_title || prev.seo_title,
          seo_description: response.data.seo_description || prev.seo_description
        }));
      } catch (error) {
        toast.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const handleTextChange = (field, lang, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handleSocialChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      await fetchSettings();
      toast.success('Content saved successfully');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const languages = [
    { code: 'sq', name: 'Albanian', flag: '🇦🇱' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white">Content Management</h1>
          <p className="text-white/50">Edit texts and social links for your website</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-brand-red hover:bg-brand-red-light"
          data-testid="save-content-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="texts" className="space-y-6">
        <TabsList className="bg-brand-dark border border-white/5">
          <TabsTrigger value="texts" className="data-[state=active]:bg-brand-red">
            <FileText className="w-4 h-4 mr-2" />
            Texts
          </TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-brand-red">
            <LinkIcon className="w-4 h-4 mr-2" />
            Social Links
          </TabsTrigger>
          <TabsTrigger value="seo" className="data-[state=active]:bg-brand-red">
            <Globe className="w-4 h-4 mr-2" />
            SEO
          </TabsTrigger>
        </TabsList>

        {/* Texts Tab */}
        <TabsContent value="texts" className="space-y-6">
          {/* Hero Text */}
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Hero Section Text</CardTitle>
              <CardDescription className="text-white/50">
                The main text shown on the homepage hero section
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Input
                      value={settings.hero_text[lang.code] || ''}
                      onChange={(e) => handleTextChange('hero_text', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="Music is the language of the soul"
                      data-testid={`hero-text-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* About Text */}
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">About Page Text</CardTitle>
              <CardDescription className="text-white/50">
                Your story and vision (shown on the About page)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Textarea
                      value={settings.about_text[lang.code] || ''}
                      onChange={(e) => handleTextChange('about_text', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white min-h-32"
                      placeholder="Tell your story..."
                      data-testid={`about-text-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTA Text */}
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Call-to-Action Text</CardTitle>
              <CardDescription className="text-white/50">
                Text shown in the CTA section on the homepage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Input
                      value={settings.cta_text[lang.code] || ''}
                      onChange={(e) => handleTextChange('cta_text', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="Ready to collaborate?"
                      data-testid={`cta-text-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Footer Text */}
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Footer Description</CardTitle>
              <CardDescription className="text-white/50">
                Short description shown in the footer
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Input
                      value={settings.footer_text[lang.code] || ''}
                      onChange={(e) => handleTextChange('footer_text', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="Music Producer & Artist"
                      data-testid={`footer-text-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Links Tab */}
        <TabsContent value="social">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Social Media Links</CardTitle>
              <CardDescription className="text-white/50">
                Links to your social media profiles (leave empty to hide)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white/70">YouTube</Label>
                  <Input
                    value={settings.social_links.youtube || ''}
                    onChange={(e) => handleSocialChange('youtube', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://youtube.com/@yourchannel"
                    data-testid="social-youtube"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Instagram</Label>
                  <Input
                    value={settings.social_links.instagram || ''}
                    onChange={(e) => handleSocialChange('instagram', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://instagram.com/yourprofile"
                    data-testid="social-instagram"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Spotify</Label>
                  <Input
                    value={settings.social_links.spotify || ''}
                    onChange={(e) => handleSocialChange('spotify', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://open.spotify.com/artist/..."
                    data-testid="social-spotify"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">TikTok</Label>
                  <Input
                    value={settings.social_links.tiktok || ''}
                    onChange={(e) => handleSocialChange('tiktok', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://tiktok.com/@yourprofile"
                    data-testid="social-tiktok"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">SoundCloud</Label>
                  <Input
                    value={settings.social_links.soundcloud || ''}
                    onChange={(e) => handleSocialChange('soundcloud', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://soundcloud.com/yourprofile"
                    data-testid="social-soundcloud"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Twitter/X</Label>
                  <Input
                    value={settings.social_links.twitter || ''}
                    onChange={(e) => handleSocialChange('twitter', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white"
                    placeholder="https://twitter.com/yourprofile"
                    data-testid="social-twitter"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">SEO Title</CardTitle>
              <CardDescription className="text-white/50">
                The title shown in search engine results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Input
                      value={settings.seo_title[lang.code] || ''}
                      onChange={(e) => handleTextChange('seo_title', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="GoldenG - Music Producer"
                      data-testid={`seo-title-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">SEO Description</CardTitle>
              <CardDescription className="text-white/50">
                The description shown in search engine results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {languages.map(lang => (
                  <div key={lang.code} className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}
                    </Label>
                    <Textarea
                      value={settings.seo_description[lang.code] || ''}
                      onChange={(e) => handleTextChange('seo_description', lang.code, e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="Official website of GoldenG - Music Producer & Artist"
                      rows={2}
                      data-testid={`seo-desc-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Content;
